/* eslint-disable react/jsx-props-no-spreading */
import React, {useMemo} from 'react';

import {autoLinkedTextAstFromString} from '../markdown/crossPlatformImports/markdownAstFromString';
import {MarkdownAstToReactComponentsMap, renderMarkdownAst} from '../markdown/markdownAstToReact';
import {NodeTransform} from '../markdown/markdownFormatterTypes';
import {MarkdownRendererErrorBoundary} from '../markdown/markdownRendererErrorBoundary';
import {AnnotationLike} from '../markdown/renderStringWithAnnotations';

/*
 * Props.
 */

export interface TextRendererProps<T extends AnnotationLike> {
  debugIdentifier: string;
  text: string;
  components: MarkdownAstToReactComponentsMap<T>;
  annotations?: ReadonlyArray<T>;
  transform?: NodeTransform;
}

/*
 * Component.
 */

export const TextRenderer = <T extends AnnotationLike>(props: TextRendererProps<T>) => {
  const {components, debugIdentifier} = props;
  return (
    <MarkdownRendererErrorBoundary
      debugIdentifier={debugIdentifier}
      FallBackComponent={components.ErrorFallbackComponent}
    >
      <RendererContent {...props} />
    </MarkdownRendererErrorBoundary>
  );
};

const RendererContent = <T extends AnnotationLike>(props: TextRendererProps<T>) => {
  const {text, components, transform, annotations} = props;

  const componentsMap = useMemo(
    () => ({
      textTransform: transform,
      ...components,
    }),
    [components, transform],
  );

  const ast = useMemo(() => autoLinkedTextAstFromString(text), [text]);

  const renderedMarkdown = useMemo(
    () => renderMarkdownAst(ast, annotations ?? [], componentsMap),
    [annotations, ast, componentsMap],
  );

  return renderedMarkdown;
};
